import React, { useState, useEffect, useMemo } from 'react';
import { useAppContext } from '../context/AppContext';
import { api } from '../services/api';
import SafeIcon from '../common/SafeIcon';
import { format, parseISO } from 'date-fns';

/**
 * Robust Date Normalization helper
 * Consistent with api.js to ensure live data matching
 */
const normalizeDate = (dateVal) => {
  if (!dateVal) return '';
  const str = String(dateVal);
  return str.split('T')[0].substring(0, 10);
};

const PrintCentre = () => {
  const { data, settings, t, getDateLocale, formatUnit } = useAppContext();
  const [targetDate, setTargetDate] = useState(new Date().toISOString().split('T')[0]);
  const [printView, setPrintView] = useState('Full');
  const [selectedCustomerId, setSelectedCustomerId] = useState('All');
  const [selectedRecipeId, setSelectedRecipeId] = useState('');
  const [productionData, setProductionData] = useState({ recipes: [], unlinkedProducts: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Fetch production summary for both 'Full' and 'Recipe Sheet' views
    const fetchProductionData = async () => {
      setLoading(true);
      try {
        const summary = await api.getProductionSummary(targetDate);
        setProductionData(summary || { recipes: [], unlinkedProducts: [] });
      } catch (error) {
        console.error("Failed to fetch production data for print", error);
        setProductionData({ recipes: [], unlinkedProducts: [] });
      }
      setLoading(false);
    };
    fetchProductionData();
  }, [targetDate, data.orders, data.orderLines]);

  const doughRecipes = useMemo(() => (data.recipes || []).filter(r => r.type === 'Dough/Base'), [data.recipes]);

  /**
   * Fixed: Delivery Data Matching Logic
   * Groups live orders by customer for the delivery note output
   */
  const deliveryData = useMemo(() => {
    const targetDateStr = normalizeDate(targetDate);
    
    // Filter orders matching the normalized target date
    const dayOrders = (data.orders || []).filter(o => 
      normalizeDate(o.delivery_date) === targetDateStr
    );

    return dayOrders.map(order => {
      const customer = (data.customers || []).find(c => String(c.id) === String(order.customer_id));
      const lines = (data.orderLines || [])
        .filter(ol => String(ol.order_id) === String(order.id))
        .map(line => ({
          ...line,
          product: (data.products || []).find(p => String(p.id) === String(line.product_id))
        }));
      return { order, customer, lines };
    }).filter(item => {
      if (!item.customer) return false;
      return selectedCustomerId === 'All' || String(item.customer.id) === String(selectedCustomerId);
    });
  }, [targetDate, selectedCustomerId, data.orders, data.orderLines, data.customers, data.products]);

  /**
   * Recipe Sheet Calculation Logic (Retained from working version)
   */
  const activeRecipe = useMemo(() => {
    if (printView !== 'Recipe Sheet' || !selectedRecipeId) return null;
    
    const recipe = (data.recipes || []).find(r => String(r.id) === String(selectedRecipeId));
    if (!recipe) return null;

    const recipesList = productionData?.recipes || [];
    const prodGroup = recipesList.find(g => String(g.recipe?.id) === String(selectedRecipeId));
    
    let rawIngredients = [];
    try {
      const rawData = recipe.baker_percentage_notes;
      if (typeof rawData === 'string' && (rawData.startsWith('[') || rawData.startsWith('{'))) {
        rawIngredients = JSON.parse(rawData);
      } else if (Array.isArray(rawData)) {
        rawIngredients = rawData;
      }
    } catch (e) {
      console.warn("[Print Calc Error] Could not parse baker_percentage_notes", e);
    }

    const totalDoughNeeded = prodGroup ? (prodGroup.totalDoughWeight || 0) : 0;
    const mixerCapacity = Number(recipe.mixer_capacity) || 0;
    const totalPercentage = rawIngredients.reduce((sum, ing) => sum + (Number(ing.percentage) || 0), 0);

    const scaleIngredients = (weight) => {
      if (weight <= 0 || totalPercentage <= 0) return [];
      const flourBasis = weight / (totalPercentage / 100);
      return rawIngredients.map(ing => ({
        ...ing,
        calculatedWeight: flourBasis * (Number(ing.percentage) / 100)
      }));
    };

    let batchList = [];
    if (mixerCapacity > 0 && totalDoughNeeded > mixerCapacity) {
      const numBatches = Math.ceil(totalDoughNeeded / mixerCapacity);
      const weightPerBatch = totalDoughNeeded / numBatches;
      for (let i = 0; i < numBatches; i++) {
        batchList.push({ id: i + 1, weight: weightPerBatch, ingredients: scaleIngredients(weightPerBatch) });
      }
    } else {
      batchList.push({ id: 1, weight: totalDoughNeeded, ingredients: scaleIngredients(totalDoughNeeded) });
    }

    return { recipe, totalDoughNeeded, mixerCapacity, batches: batchList };
  }, [printView, selectedRecipeId, data.recipes, productionData]);

  const handlePrint = () => window.print();

  return (
    <div className="bg-white min-h-screen">
      {/* Control Panel */}
      <div className="print:hidden p-6 bg-gray-50 border-b border-gray-200 mb-8 flex flex-col md:flex-row justify-between items-center gap-4 rounded-xl shadow-inner">
        <div>
          <h1 className="text-xl font-bold text-gray-800">{t('nav.printCentre')}</h1>
          <p className="text-sm text-gray-500">{t('print.centreSubtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <input 
            type="date" 
            value={targetDate} 
            onChange={(e) => setTargetDate(e.target.value)} 
            className="p-2 border border-gray-300 rounded-lg outline-none text-sm"
          />
          <select 
            value={printView} 
            onChange={(e) => setPrintView(e.target.value)}
            className="p-2 border border-gray-300 rounded-lg outline-none text-sm"
          >
            <option value="Full">{t('print.fullProduction')}</option>
            <option value="Recipe Sheet">{t('print.recipeSheet')}</option>
            <option value="Delivery Notes">{t('print.deliveryNote')}</option>
          </select>
          {printView === 'Recipe Sheet' && (
            <select 
              value={selectedRecipeId} 
              onChange={(e) => setSelectedRecipeId(e.target.value)}
              className="p-2 border border-blue-300 bg-blue-50 rounded-lg outline-none text-sm"
            >
              <option value="">{t('print.selectRecipe')}</option>
              {doughRecipes.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          )}
          {printView === 'Delivery Notes' && (
            <select 
              value={selectedCustomerId} 
              onChange={(e) => setSelectedCustomerId(e.target.value)}
              className="p-2 border border-blue-300 bg-blue-50 rounded-lg outline-none text-sm"
            >
              <option value="All">{t('print.allCustomers')}</option>
              {deliveryData.map(d => d.customer).filter((c, i, a) => c && a.findIndex(x => x.id === c.id) === i).map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          )}
          <button 
            onClick={handlePrint}
            className="bg-gray-900 text-white px-6 py-2 rounded-lg font-medium flex items-center gap-2 hover:bg-gray-800 transition-all"
          >
            <SafeIcon name="Printer" className="w-4 h-4" />
            {t('actions.print')}
          </button>
        </div>
      </div>

      {/* Printable Content Area */}
      <div className="print:block p-4 max-w-4xl mx-auto bg-white min-h-screen">
        
        {/* VIEW 1: FULL PRODUCTION SUMMARY */}
        {printView === 'Full' && (
          <div className="space-y-8 animate-fade-in">
            <div className="border-b-4 border-black pb-4 flex justify-between items-end">
              <div>
                <h1 className="text-3xl font-black uppercase tracking-tighter">{t('print.fullProduction')}</h1>
                <p className="text-sm font-bold text-gray-500 uppercase tracking-widest">{settings.company.name}</p>
              </div>
              <div className="text-right">
                <p className="text-xl font-black">{format(parseISO(targetDate), 'EEEE, MMM dd', { locale: getDateLocale() })}</p>
              </div>
            </div>

            {productionData.recipes.length === 0 && productionData.unlinkedProducts.length === 0 ? (
              <div className="py-20 text-center text-gray-400 italic border-2 border-dashed border-gray-200 rounded-xl">
                {t('production.noOrders')}
              </div>
            ) : (
              <div className="space-y-6">
                {productionData.recipes.map((group, idx) => (
                  <div key={idx} className="border-2 border-black p-4 page-break-inside-avoid">
                    <div className="flex justify-between items-center mb-4 border-b-2 border-gray-100 pb-2">
                      <h2 className="text-xl font-black uppercase">{group.recipe?.name}</h2>
                      <div className="text-right">
                        <p className="text-xs font-bold text-gray-400 uppercase">{t('production.requiredDough')}</p>
                        <p className="text-xl font-black">{(group.totalDoughWeight / 1000).toFixed(2)}kg</p>
                      </div>
                    </div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[10px] uppercase font-black text-gray-400 border-b">
                          <th className="pb-1">Product</th>
                          <th className="pb-1 text-right">Quantity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.products.map((p, pIdx) => (
                          <tr key={pIdx} className="border-b border-gray-50">
                            <td className="py-2 font-bold">{p.product?.name}</td>
                            <td className="py-2 text-right font-black text-lg">{p.qty} {formatUnit(p.product?.unit)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* VIEW 2: RECIPE SHEETS (Already Working) */}
        {printView === 'Recipe Sheet' && activeRecipe && (
          <div className="space-y-8 animate-fade-in">
            <div className="border-b-4 border-black pb-4 flex justify-between items-end">
              <div>
                <h1 className="text-3xl font-black uppercase tracking-tighter">{activeRecipe.recipe?.name}</h1>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mt-1">
                  {t('print.totalNeeded')}: {(activeRecipe.totalDoughNeeded / 1000).toFixed(2)}kg 
                </p>
              </div>
              <div className="text-right">
                <p className="text-lg font-black">{format(parseISO(targetDate), 'EEEE, MMM dd', { locale: getDateLocale() })}</p>
              </div>
            </div>
            {activeRecipe.batches.map((batch, bIdx) => (
              <div key={bIdx} className="page-break-inside-avoid border-2 border-black mb-8">
                <div className="bg-black text-white p-3 flex justify-between items-center">
                  <h2 className="text-lg font-black uppercase">{t('print.batch')} {batch.id}</h2>
                  <p className="font-black text-xl">{(batch.weight / 1000).toFixed(2)}kg</p>
                </div>
                <table className="w-full">
                  <tbody className="divide-y divide-gray-200">
                    {batch.ingredients.map((ing, iIdx) => (
                      <tr key={iIdx}>
                        <td className="p-3 font-bold text-lg">{ing.name}</td>
                        <td className="p-3 text-center text-gray-500">{ing.percentage}%</td>
                        <td className="p-3 text-right font-black text-xl">
                          {ing.calculatedWeight >= 1000 ? `${(ing.calculatedWeight / 1000).toFixed(2)}kg` : `${Math.round(ing.calculatedWeight)}g`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}

        {/* VIEW 3: DELIVERY NOTES / PACKING SLIPS */}
        {printView === 'Delivery Notes' && (
          <div className="space-y-12">
            {deliveryData.length === 0 ? (
              <div className="py-20 text-center text-gray-400 italic border-2 border-dashed border-gray-200 rounded-xl">
                {t('orders.noOrders')}
              </div>
            ) : (
              deliveryData.map(({ order, customer, lines }, idx) => (
                <div key={order.id} className="border-2 border-black p-8 page-break-after-always min-h-[280mm] flex flex-col relative overflow-hidden">
                  {/* Header */}
                  <div className="flex justify-between items-start border-b-4 border-black pb-4 mb-8">
                    <div>
                      <h2 className="text-3xl font-black uppercase tracking-tighter">{t('print.deliveryNote')}</h2>
                      <p className="text-xl font-bold text-amber-700">{settings.company.name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{t('print.deliveryDate')}</p>
                      <p className="text-2xl font-black">{format(parseISO(targetDate), 'EEE, MMM dd, yyyy', { locale: getDateLocale() })}</p>
                    </div>
                  </div>

                  {/* Customer Info */}
                  <div className="grid grid-cols-2 gap-12 mb-12">
                    <div>
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">{t('print.deliverTo')}:</p>
                      <p className="text-2xl font-black leading-tight">{customer?.name}</p>
                      <p className="text-gray-600 mt-1">{customer?.contact_name}</p>
                      <p className="text-gray-600">{customer?.phone}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">{t('print.orderRef')}:</p>
                      <p className="text-lg font-mono font-bold">#{String(order.id).toUpperCase().slice(-6)}</p>
                    </div>
                  </div>

                  {/* Products Table */}
                  <table className="w-full mb-12">
                    <thead>
                      <tr className="border-b-2 border-black text-left text-[10px] font-black uppercase tracking-widest">
                        <th className="py-3 px-1">{t('print.description')}</th>
                        <th className="py-3 px-1 text-center w-32">{t('print.qty')}</th>
                        <th className="py-3 px-1 text-right w-24">{t('print.unit')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y-2 divide-gray-100">
                      {lines.map((line, lIdx) => (
                        <tr key={lIdx}>
                          <td className="py-4 px-1">
                            <div className="font-bold text-lg">{line.product?.name || 'Unknown Product'}</div>
                            {line.product?.subcategory && <div className="text-xs text-gray-400 font-medium">{line.product.subcategory}</div>}
                          </td>
                          <td className="py-4 px-1 text-center font-black text-3xl">{line.quantity}</td>
                          <td className="py-4 px-1 text-right text-gray-500 font-bold uppercase">{formatUnit(line.product?.unit)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Notes */}
                  {order.notes && (
                    <div className="mb-12 p-4 bg-gray-50 border-2 border-gray-200 rounded-lg italic text-gray-700">
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest not-italic mb-1">Notes:</p>
                      "{order.notes}"
                    </div>
                  )}

                  {/* Signatures */}
                  <div className="mt-auto pt-12 grid grid-cols-2 gap-20">
                    <div>
                      <div className="border-b-2 border-black h-12 w-full mb-2"></div>
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{t('print.receivedBy')}</p>
                    </div>
                    <div>
                      <div className="border-b-2 border-black h-12 w-full mb-2"></div>
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{t('print.deliveredBy')}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PrintCentre;